<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { io, Socket } from 'socket.io-client';

const { t } = useI18n();

interface Props {
  deviceId: string;
}

const props = defineProps<Props>();

// 相機狀態
interface CameraStatus {
  deviceId: string;
  online: boolean;
  lastCaptureTime?: string;
  storageFree?: number;
  storageTotal?: number;
}

// 狀態
const cameraStatus = ref<CameraStatus | null>(null);
const loading = ref(true);
const capturing = ref(false);
const error = ref<string | null>(null);
const socket = ref<Socket | null>(null);
const connected = ref(false);

// 計算屬性
const isOnline = computed(() => cameraStatus.value?.online ?? false);

const storagePercent = computed(() => {
  if (!cameraStatus.value?.storageFree || !cameraStatus.value?.storageTotal) return 0;
  const used = cameraStatus.value.storageTotal - cameraStatus.value.storageFree;
  return (used / cameraStatus.value.storageTotal) * 100;
});

const formattedLastCapture = computed(() => {
  if (!cameraStatus.value?.lastCaptureTime) return '-';
  const date = new Date(cameraStatus.value.lastCaptureTime);
  return date.toLocaleString('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});

// 格式化檔案大小
function formatStorage(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// 連接 WebSocket 接收相機狀態
function connectWebSocket() {
  const wsUrl = import.meta.env.VITE_WS_URL || 'http://72.61.117.219:3000';

  console.log(`[CameraStatusCard] Connecting to WebSocket: ${wsUrl}`);

  socket.value = io(wsUrl, {
    transports: ['websocket', 'polling']
  });

  socket.value.on('connect', () => {
    console.log('[CameraStatusCard] WebSocket connected');
    connected.value = true;
    error.value = null;

    // 訂閱設備 (使用後端 realtime.types.ts 定義的事件名稱 'join_device')
    socket.value?.emit('join_device', props.deviceId);
    console.log(`[CameraStatusCard] Subscribed to device: ${props.deviceId}`);
  });

  socket.value.on('disconnect', (reason: string) => {
    console.log(`[CameraStatusCard] WebSocket disconnected: ${reason}`);
    connected.value = false;
  });

  // 接收相機狀態更新 (使用 realtime.types.ts 定義的事件名稱 'camera_status')
  socket.value.on('camera_status', (data: CameraStatus) => {
    console.log('[CameraStatusCard] Received camera status:', data);
    if (data.deviceId === props.deviceId) {
      cameraStatus.value = data;
      loading.value = false;
    }
  });

  // 拍照完成通知
  socket.value.on('capture_complete', (data: any) => {
    console.log('[CameraStatusCard] Capture complete:', data);
    if (data.deviceId === props.deviceId) {
      capturing.value = false;
      // 更新狀態
      if (cameraStatus.value) {
        cameraStatus.value.lastCaptureTime = data.timestamp;
      }
    }
  });

  socket.value.on('error', (err: any) => {
    console.error('[CameraStatusCard] WebSocket error:', err);
    error.value = err.message || 'WebSocket error';
  });
}

// 發送拍照命令
async function captureNow() {
  if (capturing.value || !isOnline.value) return;

  capturing.value = true;
  error.value = null;

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://72.61.117.219:3000';
    const token = localStorage.getItem('token');

    const response = await fetch(`${apiUrl}/api/camera/${props.deviceId}/capture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Capture failed');
    }

    const result = await response.json();
    console.log('[CameraStatusCard] Capture command sent:', result);

    // 等待拍照完成 (通過 WebSocket 收到 capture_complete)
    // 如果 10 秒內沒收到，重置狀態
    setTimeout(() => {
      if (capturing.value) {
        capturing.value = false;
        console.warn('[CameraStatusCard] Capture timeout');
      }
    }, 10000);

  } catch (err: any) {
    console.error('[CameraStatusCard] Capture error:', err);
    error.value = err.message || t('dashboard.camera.captureFailed');
    capturing.value = false;
  }
}

// 標記無相機數據狀態
function setNoDataStatus() {
  // 不使用假數據，保持 null 狀態表示無數據
  cameraStatus.value = null;
  loading.value = false;
}

// 生命週期
onMounted(() => {
  connectWebSocket();

  // 5 秒後如果還沒收到數據，顯示無數據狀態
  setTimeout(() => {
    if (loading.value) {
      console.log('[CameraStatusCard] No camera status received from WebSocket');
      setNoDataStatus();
    }
  }, 5000);
});

onUnmounted(() => {
  if (socket.value) {
    socket.value.disconnect();
    socket.value = null;
  }
});

// 監聯 deviceId 變化
watch(() => props.deviceId, (newId, oldId) => {
  if (socket.value && oldId) {
    socket.value.emit('leave_device', oldId);
    socket.value.emit('join_device', newId);
  }
  loading.value = true;
  cameraStatus.value = null;
});
</script>

<template>
  <div class="camera-status-card">
    <div class="card-header">
      <h3>📹 {{ t('dashboard.camera.title') }}</h3>
      <div class="status-indicator" :class="{ online: isOnline, offline: !isOnline }">
        {{ isOnline ? t('dashboard.camera.online') : t('dashboard.camera.offline') }}
      </div>
    </div>

    <!-- 錯誤提示 -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- 載入中 -->
    <div v-else-if="loading" class="loading">
      <div class="spinner"></div>
      <p>{{ t('dashboard.camera.loading') }}</p>
    </div>

    <!-- 相機狀態（即使沒有數據也顯示基本 UI） -->
    <div v-else class="status-content">
      <!-- 儲存空間（有數據時顯示） -->
      <div v-if="cameraStatus?.storageFree" class="storage-section">
        <div class="storage-header">
          <span class="label">{{ t('dashboard.camera.storage') }}</span>
          <span class="value">
            {{ formatStorage(cameraStatus?.storageFree) }} {{ t('dashboard.camera.free') }}
          </span>
        </div>
        <div class="storage-bar">
          <div class="storage-fill" :style="{ width: `${storagePercent}%` }" :class="{ warning: storagePercent > 80 }"></div>
        </div>
      </div>

      <!-- 上次拍照時間 -->
      <div class="last-capture-section">
        <span class="label">{{ t('dashboard.camera.lastCapture') }}</span>
        <span class="value">{{ formattedLastCapture }}</span>
      </div>

      <!-- 拍照按鈕（離線時 disabled） -->
      <div class="capture-section">
        <button
          @click="captureNow"
          :disabled="capturing || !isOnline"
          class="capture-btn"
          :class="{ capturing: capturing }"
        >
          <span v-if="capturing" class="btn-content">
            <span class="btn-spinner"></span>
            {{ t('dashboard.camera.capturing') }}
          </span>
          <span v-else class="btn-content">
            📸 {{ t('dashboard.camera.captureNow') }}
          </span>
        </button>
        <p v-if="!isOnline" class="offline-hint">{{ t('dashboard.camera.offlineHint') }}</p>
      </div>
    </div>

  </div>
</template>

<style scoped>
.camera-status-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.status-indicator {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-indicator.online {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-indicator.offline {
  background: #ffebee;
  color: #c62828;
}

.error-message {
  padding: 12px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  text-align: center;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  color: #999;
}

.no-data-icon {
  font-size: 48px;
  margin: 0 0 8px 0;
  opacity: 0.5;
}

.no-data-text {
  margin: 0;
  font-size: 14px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #666;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.last-capture-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
}

.last-capture-section .label {
  font-size: 14px;
  color: #666;
}

.last-capture-section .value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.storage-section {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
}

.storage-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.storage-header .label {
  font-size: 12px;
  color: #666;
}

.storage-header .value {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.storage-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.storage-fill {
  height: 100%;
  background: #4CAF50;
  border-radius: 4px;
  transition: width 0.3s;
}

.storage-fill.warning {
  background: #ff9800;
}

.capture-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
}

.capture-btn {
  width: 100%;
  padding: 12px 24px;
  background: #2196F3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.capture-btn:hover:not(:disabled) {
  background: #1976D2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.capture-btn:disabled {
  background: #bdbdbd;
  cursor: not-allowed;
}

.capture-btn.capturing {
  background: #ff9800;
}

.btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.capture-hint {
  margin: 0;
  font-size: 12px;
  color: #999;
  text-align: center;
}

.offline-hint {
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #ff9800;
  text-align: center;
}

@media (max-width: 768px) {
  .status-grid {
    grid-template-columns: 1fr;
  }
}
</style>
